import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import BrandName from "../components/BrandName";
import Button from "../components/Button";
import Card from "../components/Card";
import Input from "../components/Input";
import PasswordField from "../components/PasswordField";
import ErrorMessage from "../components/ErrorMessage";
import SupportLink from "../components/SupportLink";
import ShareInvite from "../components/ShareInvite";
import TwoFactorPrompt from "../components/TwoFactorPrompt";
import useForm from "../hooks/useForm";
import { register, markNewUser } from "../services/authService";
import { captureReferralFromUrl } from "../services/referralService";
import "../styles/onboarding.css";

/*
  RegisterPage — יצירת מנוי בעת הרכישה (UI_SPEC ס' 2): הלקוח בוחר שם משתמש,
  מייל וסיסמה. אחרי הרשמה מוצלחת (השרת מגדיר תוקף עד 30.8) ממשיכים לאשף
  הגדרת הגן. תוקף המנוי מנוהל בשרת.
*/
function validate(values) {
  const errors = {};
  if (!values.username.trim() || values.username.trim().length < 3) {
    errors.username = "שם המשתמש חייב להכיל לפחות 3 תווים";
  }
  if (!values.email.trim()) {
    errors.email = "צריך למלא כתובת מייל";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) {
    errors.email = "כתובת המייל אינה תקינה";
  }
  if (!values.phone.trim()) {
    errors.phone = "צריך למלא מספר טלפון";
  } else if (!/^05\d-?\d{7}$/.test(values.phone.trim())) {
    errors.phone = "מספר הטלפון אינו תקין — הפורמט: 05X-XXXXXXX";
  }
  if (!values.password || values.password.length < 8) {
    errors.password = "הסיסמה חייבת להכיל לפחות 8 תווים";
  }
  return errors;
}

function RegisterPage() {
  const navigate = useNavigate();
  // אם נרשמים תוך כדי הצטרפות לגן (?next=/join/<token>) — חוזרים לשם במקום לאשף.
  const [searchParams] = useSearchParams();
  const nextParam = searchParams.get("next");
  const safeNext = nextParam && nextParam.startsWith("/") ? nextParam : null;
  // לוכד קוד הפניה (?ref=CODE) מהקישור בכניסה לעמוד — יישמר וישלח בהרשמה,
  // כדי שהמנהלת תראה מכל לקוח/שותף כמה נרשמו דרך הקישור שלו.
  useEffect(() => {
    captureReferralFromUrl(window.location.search);
  }, []);
  const { values, errors, submitError, isSubmitting, handleChange, handleSubmit } =
    useForm({ username: "", email: "", phone: "", password: "" }, validate);

  /*
    אתגר אימות מייל פתוח: אחרי "יצירת חשבון", אם המערכת דורשת אימות מייל, השרת
    שולח קוד בן 6 ספרות למייל ומחזיר אתגר (בלי טוקן). עד שהקוד מאומת מציגים את
    מסך הקוד (אותו רכיב של האימות הדו-שלבי) במקום טופס ההרשמה.
  */
  const [challenge, setChallenge] = useState(null);

  const finishRegister = () => {
    markNewUser(); // משתמש חדש — יראה פעם אחת את פופאפ הברוכים-הבאים
    navigate(safeNext || "/onboarding");
  };

  const onSubmit = handleSubmit(async (formValues) => {
    const auth = await register({
      username: formValues.username.trim(),
      email: formValues.email.trim(),
      phone: formValues.phone.trim(),
      password: formValues.password,
    });
    // אימות מייל נדרש — מציגים מסך קוד; הכניסה תושלם בהזנת הקוד.
    if (auth?.twoFactorRequired) {
      setChallenge(auth);
      return;
    }
    finishRegister();
  });

  return (
    <div className="auth-page">
      <h1 className="auth-page__logo">
        <BrandName />
      </h1>
      <p className="auth-page__slogan">ארגון חכם. ניהול מנצח</p>
      <Card title={challenge ? "אימות כתובת המייל" : "נעים להכיר! נפתח לך חשבון"}>
        {challenge ? (
          <>
            <p className="auth-page__hint" style={{ marginTop: 0 }}>
              כדי להשלים את ההרשמה, הזינו את הקוד ששלחנו למייל שלכם. אם לא מצאתם
              אותו — כדאי לבדוק גם בתיקיית ה"ספאם"/"קידומי מכירות".
            </p>
            <TwoFactorPrompt
              challenge={challenge}
              onSuccess={finishRegister}
              onCancel={() => setChallenge(null)}
            />
          </>
        ) : (
        <form onSubmit={onSubmit} noValidate>
          <Input
            id="register-username"
            name="username"
            label="שם משתמש"
            autoComplete="username"
            value={values.username}
            onChange={handleChange}
            error={errors.username}
          />
          <Input
            id="register-email"
            name="email"
            label="כתובת מייל"
            type="email"
            autoComplete="email"
            value={values.email}
            onChange={handleChange}
            error={errors.email}
          />
          <Input
            id="register-phone"
            name="phone"
            label="טלפון נייד"
            type="tel"
            dir="ltr"
            autoComplete="tel"
            placeholder="050-1234567"
            value={values.phone}
            onChange={handleChange}
            error={errors.phone}
          />
          <PasswordField
            id="register-password"
            name="password"
            label="סיסמה"
            value={values.password}
            onChange={handleChange}
            error={errors.password}
          />
          {submitError && <ErrorMessage message={submitError} />}
          <div className="auth-page__actions">
            <Button type="submit" isLoading={isSubmitting}>
              יצירת חשבון
            </Button>
          </div>
          <p className="auth-page__hint">
            בלחיצה על "יצירת חשבון" את מסכימה ל
            <Link to="/privacy">מדיניות הפרטיות</Link> ול
            <Link to="/terms">תנאי השימוש</Link>.
          </p>
          <p className="auth-page__hint">
            כבר יש לך חשבון? <Link to="/login">לכניסה עם שם משתמש או Google</Link>
          </p>
          <ShareInvite
            url={window.location.origin}
            title="הזמנה ל-VaddyGo"
            message="מנהלים ועד הורים? VaddyGo עוזרת לנהל גבייה, תשלומים ותקשורת עם ההורים בקלות. מוזמנים להתחיל:"
          />
        </form>
        )}
      </Card>
      <SupportLink />
    </div>
  );
}

export default RegisterPage;
